import { Box, Typography } from '@mui/material';
import { Package } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router';

import { dataRecordPath } from '@/admin/data/routes';
import { Badge, Card, Icon, MISSING_VALUE } from '@/ui/kit';
import { fontStacks } from '@/ui/tokens';

import {
  categoryLabel,
  describeCurrentLocation,
  describeExpiry,
  describePortionContent,
  describeStatus,
  formatMoment,
  formatQuantity,
} from './stockPresentation';
import type { StockBatch } from './stockTypes';

/**
 * Fiche d'un lot : code, statut, echeance, et quatre reperes.
 *
 * Tout vient de la reconstitution du journal par le serveur. Les reperes sont une
 * liste de definitions (`<dl>`) : chaque valeur est lue avec son intitule, et le
 * lecteur d'ecran entend l'intitule d'abord. L'oeil, lui, lit la valeur d'abord,
 * comme dans la maquette (`column-reverse`).
 */

const ORIGINS: Readonly<Record<string, string>> = {
  pumping: 'issu d’un tirage',
  manual: 'saisi à la main',
};

const Tile = ({ label, children }: { label: string; children: ReactNode }): ReactElement => (
  <Box
    sx={{
      flex: '1 1 180px',
      minWidth: 0,
      p: 1.75,
      borderRadius: '12px',
      bgcolor: 'tokens.surfaceMuted',
      display: 'flex',
      flexDirection: 'column-reverse',
      gap: 0.25,
    }}
  >
    <Typography component="dt" sx={{ fontSize: 12, color: 'tokens.textMuted', overflowWrap: 'anywhere' }}>
      {label}
    </Typography>
    <Typography component="dd" sx={{ m: 0, fontSize: 18, fontWeight: 700, lineHeight: '24px', overflowWrap: 'anywhere' }}>
      {children}
    </Typography>
  </Box>
);

export const BatchSummary = ({
  batch,
  scopeId,
  now,
}: {
  batch: StockBatch;
  /** Périmètre du journal du lot, celui que la recherche a rendu. */
  scopeId: string;
  now: Date;
}): ReactElement => {
  const status = describeStatus(batch.status);
  const expiry = describeExpiry(batch, now);
  const content = describePortionContent(batch);
  const origin = batch.origin === null ? null : (ORIGINS[batch.origin] ?? batch.origin);
  const subtitle = [categoryLabel(batch.category), content, origin]
    .filter((part): part is string => part !== null && part !== '' && part !== MISSING_VALUE)
    .join(' · ');

  return (
    <Card>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box
          sx={{
            display: 'inline-flex',
            p: 1.5,
            borderRadius: '14px',
            bgcolor: 'tokens.infoSoft',
            color: 'tokens.infoText',
          }}
        >
          <Icon icon={Package} size={24} />
        </Box>
        <Box sx={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              component="h2"
              sx={{ fontFamily: fontStacks.mono, fontSize: 20, fontWeight: 700, overflowWrap: 'anywhere' }}
            >
              {batch.code ?? 'Lot sans code lisible'}
            </Typography>
            {status === null ? null : <Badge label={status.label} tone={status.tone} dot />}
            {expiry === null ? null : <Badge label={expiry.label} tone={expiry.tone} />}
          </Box>
          {subtitle === '' ? null : (
            <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>{subtitle}</Typography>
          )}
        </Box>
      </Box>

      <Box component="dl" sx={{ m: 0, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Tile
          label={
            batch.initialQuantity === null
              ? 'Restant (quantité initiale illisible)'
              : `Restant sur ${formatQuantity(batch.initialQuantity, batch.unit)}`
          }
        >
          {formatQuantity(batch.remainingQuantity, batch.unit)}
        </Tile>
        <Tile label="Lieu actuel">{describeCurrentLocation(batch)}</Tile>
        <Tile label={batch.unit === 'portion' ? 'Date de préparation (référence)' : 'Date du lait (référence)'}>
          {formatMoment(batch.pumpedAt)}
        </Tile>
        <Tile label={`Échéance · règle ${batch.expiryRuleCode ?? MISSING_VALUE}`}>{formatMoment(batch.expiresAt)}</Tile>
      </Box>

      <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
        Les notes et commentaires ne sont pas affichés ici : cette vue ne lit aucun texte libre. L’explorateur les montre
        selon votre rôle (masqués pour le rôle support).{' '}
        <Box
          component={Link}
          to={dataRecordPath(scopeId, 'milk_batch', batch.id)}
          sx={{
            color: 'tokens.accentPinkStrong',
            fontWeight: 600,
            textUnderlineOffset: 3,
            '&:focus-visible': { outline: '2px solid', outlineColor: 'tokens.primary', outlineOffset: 2, borderRadius: '4px' },
          }}
        >
          Ouvrir ce lot dans l’explorateur
        </Box>
      </Typography>
    </Card>
  );
};
